import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Route, Routes } from 'react-router';
import { Header, Footer } from '@ui/siteStructure';
import { SidebarAds } from '@ui/ads/components/SidebarAds';
import { DeviceType } from '@repo/types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CmsPage } from './pages/CmsPage';
import { ArticlePage } from './pages/ArticlePage';
import { FixturesPage } from './pages/FixturesPage';
import { ResultsPage } from './pages/ResultsPage';
import { TeamPage } from './pages/TeamPage';
import { MatchPage } from './pages/MatchPage/MatchPage';
import { NewsletterConfirm } from './pages/NewsletterConfirm';
import { useScrollToTop } from './utils/useScrollToTop';
import { ScrollToTop } from '@ui/common/components/ScrollToTop/ScrollToTop';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { ads } from './constants';
import './styles/index.css';
import './styles/theme.css';
import './styles/breakpoints.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { ClubPage } from './pages/ClubPage';
import { MatchCentrePage } from './pages/MatchCentre/MatchCentre';
import { useGlobalScrollEvents } from './utils/useGlobalScrollEvents';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
  }
}

export function ClientApp() {
  const deviceType = useDeviceType();
  useScrollToTop();

  if (!import.meta.env.PROD && typeof window !== 'undefined') {
    window.__TANSTACK_QUERY_CLIENT__ = queryClient;
  }

  // Subscribe to url listeners for anchor hash changes that trigger smooth scrolling
  useGlobalScrollEvents();

  return (
    <HelmetProvider>
      <Helmet prioritizeSeoTags>
        <title>{import.meta.env.VITE_SITE_NAME}</title>
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:site_name" content={import.meta.env.VITE_SITE_NAME} />
        <meta name="twitter:site" content={import.meta.env.VITE_TWITTER_SITE} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <div className="page-wrapper">
          <Header />
          <div className="main-content-wrapper">
            <main>
              <Routes>
                <Route path="/match/:id" element={<MatchPage />} />
                <Route path="/clubs/:slug" element={<ClubPage />} />
                <Route path="/teams/:slug" element={<TeamPage />} />
                <Route path="/articles/:slug" element={<ArticlePage />} />
                <Route path="/results" element={<ResultsPage />} />
                <Route path="/fixtures" element={<FixturesPage />} />
                <Route path="/live" element={<MatchCentrePage />} />
                <Route path="/newsletter-confirm" element={<NewsletterConfirm />} />
                <Route path="*" element={<CmsPage />} />
              </Routes>
              <ScrollToTop />
            </main>
            {deviceType === DeviceType.desktop && <SidebarAds ads={ads} />}
          </div>
          <Footer />
        </div>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export function ServerApp() {
  return (
    <Helmet prioritizeSeoTags>
      <title>{import.meta.env.VITE_SITE_NAME}</title>
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content={import.meta.env.VITE_SITE_NAME} />
      {import.meta.env.VITE_TWITTER_SITE && <meta name="twitter:site" content={import.meta.env.VITE_TWITTER_SITE} />}
      {import.meta.env.VITE_TWITTER_SITE && <meta name="twitter:card" content="summary_large_image" />}
    </Helmet>
  );
}
